import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {convertTaskDefinitionVersionToSpec} from '../processFlowchartConfigEditorNetworkUtils';
import {Transition} from '../types/transition/TransitionTypes';
import {ConfigurableTaskDefinitionGQL} from '../useAllSchemasById';

export interface NodeDisplayInfo {
  name: string;
  icon?: BenchlingIconName;
}

export interface TransitionDisplayInfo {
  id: string;
  fromNodes: Array<NodeDisplayInfo>;
  toNode: NodeDisplayInfo;
  isPlanned: boolean;
}

function getNodeDisplayInfo(
  taskSchemaId: string,
  isFlowchartOutput: boolean,
  taskSchemaIdToDefinition: Map<string, ConfigurableTaskDefinitionGQL>,
  editorMode: FlowchartEditorMode
): NodeDisplayInfo | null {
  const definition = taskSchemaIdToDefinition.get(taskSchemaId);
  if (definition == null) {
    return null;
  }

  const processTaskSchema = definition.latestVersion.processTaskSchema;
  const spec = convertTaskDefinitionVersionToSpec(definition.latestVersion, processTaskSchema);
  const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(processTaskSchema.executionType);

  return {
    name: adapter.getNodeName(spec, editorMode, isFlowchartOutput),
    icon: adapter.getNodeIcon(spec, isFlowchartOutput) ?? undefined,
  };
}

function getNodeDisplayInfoKey(taskSchemaId: string, isFlowchartOutput: boolean): string {
  return isFlowchartOutput ? `${taskSchemaId}:output` : taskSchemaId;
}

function buildNodeDisplayInfoMap(
  metadataTransitions: Record<string, Transition>,
  ptdvIdToTaskSchemaId: Record<string, string>,
  taskSchemaIdToDefinition: Map<string, ConfigurableTaskDefinitionGQL>,
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL,
  editorMode: FlowchartEditorMode
): Map<string, NodeDisplayInfo> {
  const keysToResolve = new Set<string>();

  for (const transition of Object.values(metadataTransitions)) {
    const targetId = ptdvIdToTaskSchemaId[transition.targetPTDVId];
    if (targetId != null) {
      keysToResolve.add(getNodeDisplayInfoKey(targetId, targetId === rootTaskSchema.id));
    }
    for (const sourcePTDVId of transition.sourcePTDVIds) {
      const sourceId = ptdvIdToTaskSchemaId[sourcePTDVId];
      if (sourceId != null) {
        keysToResolve.add(getNodeDisplayInfoKey(sourceId, false));
      }
    }
  }

  // Root task schema has the same task definition version but represents both start and end nodes.
  const map = new Map<string, NodeDisplayInfo>();
  for (const key of keysToResolve) {
    const isOutput = key.endsWith(':output');
    const taskSchemaId = isOutput ? key.slice(0, -':output'.length) : key;
    const info = getNodeDisplayInfo(taskSchemaId, isOutput, taskSchemaIdToDefinition, editorMode);
    if (info != null) {
      map.set(key, info);
    }
  }
  return map;
}

interface ResolvedNode {
  info: NodeDisplayInfo;
  isRoot: boolean;
}

interface TransitionWithSortKey extends TransitionDisplayInfo {
  sourceIsRoot: boolean;
  targetIsRoot: boolean;
}

function getNodeDisplayInfoForPTDV(
  ptdvId: string,
  treatRootAsOutput: boolean,
  ptdvIdToTaskSchemaId: Record<string, string>,
  displayInfoByKey: Map<string, NodeDisplayInfo>,
  rootTaskSchemaId: string
): ResolvedNode | null {
  const taskSchemaId = ptdvIdToTaskSchemaId[ptdvId];
  if (taskSchemaId == null) {
    return null;
  }
  const isRoot = taskSchemaId === rootTaskSchemaId;
  const key = getNodeDisplayInfoKey(taskSchemaId, treatRootAsOutput && isRoot);
  const info = displayInfoByKey.get(key);
  return info != null ? {info, isRoot} : null;
}

function collectTransitionEntries(
  metadataTransitions: Record<string, Transition>,
  ptdvIdToTaskSchemaId: Record<string, string>,
  displayInfoByKey: Map<string, NodeDisplayInfo>,
  rootTaskSchemaId: string
): Array<TransitionWithSortKey> {
  const result: Array<TransitionWithSortKey> = [];

  for (const transition of Object.values(metadataTransitions)) {
    const target = getNodeDisplayInfoForPTDV(
      transition.targetPTDVId,
      true,
      ptdvIdToTaskSchemaId,
      displayInfoByKey,
      rootTaskSchemaId
    );
    if (target == null) {
      continue;
    }

    const fromNodes: Array<NodeDisplayInfo> = [];
    let sourceIsRoot = false;
    for (const sourcePTDVId of transition.sourcePTDVIds) {
      const source = getNodeDisplayInfoForPTDV(
        sourcePTDVId,
        false,
        ptdvIdToTaskSchemaId,
        displayInfoByKey,
        rootTaskSchemaId
      );
      if (source != null) {
        fromNodes.push(source.info);
        if (source.isRoot) {
          sourceIsRoot = true;
        }
      }
    }

    if (fromNodes.length === 0) {
      continue;
    }

    result.push({
      id: transition.id,
      fromNodes,
      toNode: target.info,
      isPlanned: transition.isPlanned,
      sourceIsRoot,
      targetIsRoot: target.isRoot,
    });
  }

  return result;
}

/**
 * Sorts transitions so those originating from the root (start) node appear first,
 * and those targeting the root (end/output) node appear last.
 */
function compareTransitionsByRootProximity(a: TransitionWithSortKey, b: TransitionWithSortKey): number {
  if (a.sourceIsRoot !== b.sourceIsRoot) {
    return a.sourceIsRoot ? -1 : 1;
  }
  if (a.targetIsRoot !== b.targetIsRoot) {
    return a.targetIsRoot ? 1 : -1;
  }
  return 0;
}

export function buildTransitionDisplayInfos(
  metadataTransitions: Record<string, Transition>,
  ptdvIdToTaskSchemaId: Record<string, string>,
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>,
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL,
  editorMode: FlowchartEditorMode
): Array<TransitionDisplayInfo> {
  const taskSchemaIdToDefinition = new Map(
    taskDefinitionsForSidepanel.map((def) => [def.latestVersion.processTaskSchema.id, def])
  );

  const displayInfoByKey = buildNodeDisplayInfoMap(
    metadataTransitions,
    ptdvIdToTaskSchemaId,
    taskSchemaIdToDefinition,
    rootTaskSchema,
    editorMode
  );

  const result = collectTransitionEntries(
    metadataTransitions,
    ptdvIdToTaskSchemaId,
    displayInfoByKey,
    rootTaskSchema.id
  );

  result.sort(compareTransitionsByRootProximity);

  return result;
}
